import React, { useState } from 'react';
import styled from 'styled-components';
import { FaMapMarkerAlt, FaWhatsapp, FaEnvelope, FaClock, FaPaw, FaPhone } from 'react-icons/fa';
import Icon from './Icon';
import ThankYouModal from './ThankYouModal';
import SectionTitle from './SectionTitle';

const ContactSection = styled.section`
  padding: 80px 0;
  background-color: #f8f9fa;
  position: relative;
  overflow: hidden;
  width: 100%;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    padding: 60px 0;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
  position: relative;
  z-index: 2;
  width: 100%;
  box-sizing: border-box;
`;

const ContactContent = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
  width: 100%;
  box-sizing: border-box;
  
  @media (min-width: 768px) {
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  }
`;

const ContactInfo = styled.div`
  display: grid;
  gap: 2rem;
  order: 2;
  width: 100%;
  box-sizing: border-box;
  
  @media (min-width: 768px) {
    order: 1;
  }
`;

const InfoCard = styled.div`
  background: #fff;
  padding: 1.5rem;
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s ease;
  width: 100%;
  box-sizing: border-box;
  
  @media (min-width: 768px) {
    padding: 2.5rem;
  }
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.1);
  }
  
  h3 {
    font-size: 1.3rem;
    margin-bottom: 1.5rem;
    color: #2c3e50;
    font-weight: 600;
    position: relative;
    padding-bottom: 0.75rem;
    
    @media (min-width: 768px) {
      font-size: 1.5rem;
    }
    
    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      width: 40px;
      height: 3px;
      background-color: #ff9d5c;
      border-radius: 2px;
    }
  }
  
  p {
    display: flex;
    align-items: center;
    gap: 1rem;
    color: #666;
    margin-bottom: 1.25rem;
    font-size: 1rem;
    word-break: break-word;
    
    @media (min-width: 768px) {
    font-size: 1.05rem;
    }
    
    &:last-child {
      margin-bottom: 0;
    }
    
    .icon-wrapper {
      min-width: 40px;
      height: 40px;
      background-color: #f8f9fa;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #ff9d5c;
      flex-shrink: 0;
    }
  }
`;

const Subtitle = styled.p`
  color: #6C7A89;
  font-size: 1.1rem;
  max-width: 800px;
  margin: 1.5rem auto;
  text-align: center;
  padding: 0 1rem;
  box-sizing: border-box;
  
  @media (min-width: 768px) {
    font-size: 1.2rem;
  }
`;

const ContactForm = styled.form`
  background: #fff;
  padding: 1.5rem;
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  border: 1px solid #f5f5f5;
  order: 1;
  width: 100%;
  box-sizing: border-box;
  
  @media (min-width: 768px) {
    order: 2;
    padding: 2.5rem;
  }
  
  h3 {
    font-size: 1.3rem;
    margin-bottom: 1.5rem;
    color: #2c3e50;
    font-weight: 600;
    position: relative;
    padding-bottom: 0.75rem;
    
    @media (min-width: 768px) {
      font-size: 1.5rem;
    }
    
    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      width: 40px;
      height: 3px;
      background-color: #ff9d5c;
      border-radius: 2px;
    }
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
  
  label {
    display: block;
    margin-bottom: 0.5rem;
    color: #2c3e50;
    font-weight: 500;
  }
  
  input,
  textarea {
    width: 100%;
    padding: 0.75rem 1rem;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    font-size: 1rem;
    transition: border-color 0.3s ease;
    
    &:focus {
      outline: none;
      border-color: #ff9d5c;
    }
  }
  
  textarea {
    min-height: 120px;
    resize: vertical;
  }
`;

const SubmitButton = styled.button`
  background: #25D366;
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;
  margin-top: 1rem;

  &:hover {
    background: #128C7E;
    transform: translateY(-2px);
  }

  &:disabled {
    background: #128C7E;
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const DogImageTitle = styled.img`
  width: 100px;  
  object-fit: contain;
  position: absolute;  
  right: 20px;
  top: 20px;

  @media (max-width: 768px) {
    width: 100px;
    position: relative;
    display: block;    
    margin: 0 auto;
  }
`;

const MapButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  background: #4285f4;
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 600;
  font-size: 1rem;
  margin-bottom: 1rem;
  transition: all 0.3s ease;
    border: none;
  box-shadow: 0 2px 8px rgba(66, 133, 244, 0.2);
  width: fit-content;

  &:hover {
    background: #3367d6;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(66, 133, 244, 0.3);
  }
`;

const MapContainer = styled.div`
  order: 3;
  width: 100%;
  margin-top: 2rem;
  grid-column: 1 / -1;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-sizing: border-box;
  
  iframe {
    max-width: 100%;
    border-radius: 15px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  }
`;

const ContactMap = styled.div`
  width: 100%;
  padding: 0.5rem;
  background: #fff;
  border-radius: 15px;
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 20px 50px rgba(0, 0, 0, 0.15);
  }
  
  iframe {
    width: 100%;
    height: 400px;
    border: none;
    border-radius: 12px;
  }
`;

interface PawDecorationProps {
  $delay: string;
  $top: string;
  $left: string;
  $rotation: string;
}

const PawDecoration = styled.div<PawDecorationProps>`
  position: absolute;
  color: #ff9d5c;
  opacity: 0.1;
  transform: rotate(${(props) => props.$rotation});
  top: ${(props) => props.$top};
  left: ${(props) => props.$left};
  animation: float 3s ease-in-out ${(props) => props.$delay} infinite;
  z-index: 1;
`;

interface ContactProps {
  id?: string;
}

const Contact: React.FC<ContactProps> = ({ id }) => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showThankYouModal, setShowThankYouModal] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Dispara conversão Google Ads
    if (window.gtag_report_conversion) window.gtag_report_conversion();
    
    // Formata a mensagem para o WhatsApp
    const whatsappMessage = `*Nova mensagem do site Estima Pet*\n\n` +
      `*Nome:* ${formData.name}\n` +
      `*Telefone:* ${formData.phone}\n` +
      `*Mensagem:* ${formData.message}\n\n` +
      `_Mensagem enviada através do site www.clinicaestimapet.com.br_`;

    // Codifica a mensagem para URL
    const encodedMessage = encodeURIComponent(whatsappMessage);
    
    // Número do WhatsApp da clínica
    const whatsappNumber = '77999441369';
    
    // Cria o link do WhatsApp
    const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodedMessage}`;
    
    // Abre o WhatsApp em uma nova aba
    window.open(whatsappUrl, '_blank');
    
    // Limpa o formulário
    setFormData({
      name: '',
      phone: '',
      message: ''
    });
    
    setIsSubmitting(false);
    setShowThankYouModal(true);
  };

  return (
    <ContactSection id={id}>
      <Container>
        <SectionTitle>Entre em Contato</SectionTitle>
        <Subtitle>
          <DogImageTitle src="/images/contact.png" alt="Cachorro feliz" />
          <span>Estamos aqui para cuidar do seu pet com todo carinho e atenção</span>
        </Subtitle>

        <ContactContent>
          <ContactForm onSubmit={handleSubmit}>
            <h3>Envie uma Mensagem</h3>
            <FormGroup>
              <label htmlFor="name">Nome</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="Seu nome completo"
              />
            </FormGroup>
            <FormGroup>
              <label htmlFor="phone">Telefone</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                placeholder="(00) 00000-0000"
              />
            </FormGroup>
            <FormGroup>
              <label htmlFor="message">Mensagem</label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                placeholder="Como podemos ajudar seu pet?"
                rows={5}
              />
            </FormGroup>
            <SubmitButton type="submit" disabled={isSubmitting}>
              <Icon icon={FaWhatsapp} size={20} color="white" />
              {isSubmitting ? 'Enviando...' : 'Enviar Mensagem'}
            </SubmitButton>
          </ContactForm>

          <ContactInfo>
            <InfoCard>
              <h3>Endereço</h3>
              <p>
                <span className="icon-wrapper">
                  <Icon icon={FaMapMarkerAlt} size={18} color="currentColor" />
                </span>
                Rua Risolene Lima Dias, 25A – esquina com a Avenida João Paulo, Brumado-BA
              </p>
              <p>
                <span className="icon-wrapper">
                  <Icon icon={FaPhone} size={18} color="currentColor" />
                </span>
                (77) 99944-1369
              </p>
              <p>
                <span className="icon-wrapper">
                  <Icon icon={FaEnvelope} size={18} color="currentColor" />
                </span>
                clinicaestimapet@gmail.com
              </p>
            </InfoCard>
            <InfoCard>
              <h3>Horário de Funcionamento</h3>
              <p>
                <span className="icon-wrapper">
                  <Icon icon={FaClock} size={18} color="currentColor" />
                </span>
                Segunda a Sexta: 8h às 18h
              </p>
              <p>
                <span className="icon-wrapper">
                  <Icon icon={FaClock} size={18} color="currentColor" />
                </span>
                Sábado: 8h às 12h
              </p>
              <p>
                <span className="icon-wrapper">
                  <Icon icon={FaClock} size={18} color="currentColor" />
                </span>
                Domingo: Fechado
              </p>
            </InfoCard>
          </ContactInfo>

          <MapContainer>
            <MapButton 
              href="https://www.google.com/maps/dir/?api=1&destination=-14.207727, -41.677639" 
              target="_blank" 
              rel="noopener noreferrer"
              onClick={e => { window.gtag_report_conversion('https://www.google.com/maps/dir/?api=1&destination=-14.207727, -41.677639'); }}
            >
              <Icon icon={FaMapMarkerAlt} size={18} color="white" />
              Ir para a clínica
            </MapButton>
          <ContactMap>
            <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3867.7794710054686!2d-41.677837499999995!3d-14.2076871!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x7444b51860fc46f%3A0x4d0fadbbae438be3!2sEstima%20Pet%20-%20Clinica%20Veterin%C3%A1ria!5e0!3m2!1spt-BR!2sbr!4v1745978130717!5m2!1spt-BR!2sbr"
              allowFullScreen
              loading="lazy"
                title="Localização da Clínica"
              ></iframe>
          </ContactMap>
          </MapContainer>
        </ContactContent>
      </Container>
      
      {/* Patinhas decorativas */}
      <PawDecoration $delay="0s" $top="20%" $left="10%" $rotation="15deg">
        <Icon icon={FaPaw} size={40} color="currentColor" />
      </PawDecoration>
      <PawDecoration $delay="1s" $top="40%" $left="80%" $rotation="-10deg">
        <Icon icon={FaPaw} size={30} color="currentColor" />
      </PawDecoration>
      <PawDecoration $delay="2s" $top="70%" $left="20%" $rotation="25deg">
        <Icon icon={FaPaw} size={35} color="currentColor" />
      </PawDecoration>
      <PawDecoration $delay="1.5s" $top="60%" $left="70%" $rotation="-20deg">
        <Icon icon={FaPaw} size={25} color="currentColor" />
      </PawDecoration>

      <ThankYouModal 
        isOpen={showThankYouModal}
        onClose={() => setShowThankYouModal(false)}
      />
    </ContactSection>
  );
};

export default Contact; 